import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, ArrowRight, RotateCcw } from "lucide-react";
import { resolveCtaUrl } from "../../lib/cta";

const RANGES = [
  { key: "magro", max: 18.5, color: "#7C8AA5" },
  { key: "normal", max: 25, color: "#8DAE6F" },
  { key: "sobrepeso", max: 30, color: "#C5B358" },
  { key: "obesidade", max: 60, color: "#B5483F" },
];

function classify(bmi) {
  return RANGES.find((r) => bmi < r.max) || RANGES[RANGES.length - 1];
}

function formatBmi(value) {
  return Number.isFinite(value) ? value.toFixed(1).replace(".", ",") : "—";
}

export default function BmiCalculator({ data, content }) {
  const [sex, setSex] = useState("female");
  const [age, setAge] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [showResult, setShowResult] = useState(false);

  const ctaHref = resolveCtaUrl(content);

  const bmi = useMemo(() => {
    const h = parseFloat(height) / 100;
    const w = parseFloat(weight);
    if (!h || !w || h <= 0 || w <= 0) return null;
    return w / (h * h);
  }, [height, weight]);

  const category = bmi ? classify(bmi) : null;
  const catData = category && data?.[`category${category.key.charAt(0).toUpperCase() + category.key.slice(1)}`];

  // Position the indicator on the scale (15..40 range)
  const scaleMin = 15;
  const scaleMax = 40;
  const indicatorPct = bmi
    ? Math.max(0, Math.min(100, ((bmi - scaleMin) / (scaleMax - scaleMin)) * 100))
    : 0;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!bmi) return;
    setShowResult(true);
    setTimeout(() => {
      document.getElementById("bmi-result")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const onReset = () => {
    setShowResult(false);
    setAge("");
    setHeight("");
    setWeight("");
  };

  const SexButton = ({ value, label }) => (
    <button
      type="button"
      data-testid={`bmi-sex-${value}`}
      onClick={() => setSex(value)}
      className={`flex-1 py-3 text-xs tracking-[0.2em] uppercase font-semibold border transition-colors ${
        sex === value
          ? "bg-onyx text-alabaster border-onyx"
          : "bg-white text-onyx border-border hover:border-onyx"
      }`}
    >
      {label}
    </button>
  );

  return (
    <section id="bmi" data-testid="bmi-section" className="py-24 lg:py-32 bg-alabaster">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center justify-center gap-2 mb-4">
            <Calculator size={16} className="text-gold" strokeWidth={1.5} />
            <p className="overline" data-testid="bmi-overline">{data.overline}</p>
          </div>
          <h2
            data-testid="bmi-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-onyx leading-tight"
          >
            {data.title}
          </h2>
          <p className="mt-4 text-onyx/70" data-testid="bmi-subtitle">{data.subtitle}</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white border border-border shadow-sm overflow-hidden"
        >
          {/* Form */}
          <form
            data-testid="bmi-form"
            onSubmit={onSubmit}
            className="p-8 lg:p-12 space-y-6 border-b lg:border-b-0 lg:border-r border-border"
          >
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-onyx/60 mb-3">Sexo</label>
              <div className="flex gap-3">
                <SexButton value="female" label={data.sexLabelFemale || "Mulher"} />
                <SexButton value="male" label={data.sexLabelMale || "Homem"} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-onyx/60 mb-2">{data.ageLabel || "Idade"}</label>
                <input
                  data-testid="bmi-age"
                  type="number"
                  inputMode="numeric"
                  min={5}
                  max={120}
                  required
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="30"
                  className="w-full px-3 py-3 border border-border bg-white text-onyx focus:outline-none focus:border-gold text-base text-center"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-onyx/60 mb-2">{data.heightLabel || "Altura (cm)"}</label>
                <input
                  data-testid="bmi-height"
                  type="number"
                  inputMode="numeric"
                  min={80}
                  max={250}
                  required
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="170"
                  className="w-full px-3 py-3 border border-border bg-white text-onyx focus:outline-none focus:border-gold text-base text-center"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-onyx/60 mb-2">{data.weightLabel || "Peso (kg)"}</label>
                <input
                  data-testid="bmi-weight"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min={20}
                  max={400}
                  required
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="70"
                  className="w-full px-3 py-3 border border-border bg-white text-onyx focus:outline-none focus:border-gold text-base text-center"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={!bmi}
              data-testid="bmi-submit"
              className="group w-full inline-flex items-center justify-center gap-3 py-4 bg-onyx text-alabaster text-xs tracking-[0.25em] uppercase font-semibold hover:bg-gold hover:text-onyx transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {data.ctaLabel || "Calcular Meu IMC"}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </button>

            {data.disclaimer && (
              <p className="text-xs text-onyx/40 leading-relaxed">{data.disclaimer}</p>
            )}
          </form>

          {/* Result */}
          <div id="bmi-result" className="p-8 lg:p-12 bg-alabaster relative flex flex-col">
            <AnimatePresence mode="wait">
              {!showResult || !bmi ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex-1 flex flex-col items-center justify-center text-center min-h-[320px]"
                >
                  <Calculator size={48} className="text-onyx/15 mb-4" strokeWidth={1} />
                  <p className="font-serif text-2xl text-onyx/40">Aguardando seus dados</p>
                  <p className="text-sm text-onyx/40 mt-2 max-w-xs">
                    Preencha os campos ao lado e clique em calcular para ver seu resultado aqui.
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="flex-1 flex flex-col"
                  data-testid="bmi-result-card"
                >
                  <p className="overline text-onyx/60 mb-2">Seu resultado</p>
                  <div className="flex items-baseline gap-3">
                    <span
                      data-testid="bmi-value"
                      className="font-serif text-7xl lg:text-8xl font-light text-onyx leading-none"
                    >
                      {formatBmi(bmi)}
                    </span>
                    <span className="text-sm text-onyx/50 tracking-wide">kg/m²</span>
                  </div>

                  <div className="mt-4 inline-flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    <span
                      data-testid="bmi-category"
                      className="text-sm tracking-[0.2em] uppercase font-bold"
                      style={{ color: category.color }}
                    >
                      {catData?.label || category.key}
                    </span>
                  </div>

                  {/* Scale visualization */}
                  <div className="mt-6">
                    <div className="relative h-2 rounded-full overflow-hidden flex">
                      <div className="flex-1" style={{ background: "#7C8AA5" }} />
                      <div className="flex-1" style={{ background: "#8DAE6F" }} />
                      <div className="flex-1" style={{ background: "#C5B358" }} />
                      <div className="flex-1" style={{ background: "#B5483F" }} />
                      <span
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
                        style={{ left: `${indicatorPct}%` }}
                      >
                        <span className="block w-4 h-4 rounded-full bg-onyx border-2 border-alabaster shadow-lg" />
                      </span>
                    </div>
                    <div className="mt-2 flex justify-between text-[10px] tracking-wider uppercase text-onyx/40">
                      <span>Magro</span>
                      <span>Normal</span>
                      <span>Sobrepeso</span>
                      <span>Obesidade</span>
                    </div>
                  </div>

                  <p className="mt-6 text-onyx/80 leading-relaxed text-sm" data-testid="bmi-message">
                    {catData?.message}
                  </p>

                  <div className="mt-auto pt-8 flex flex-col sm:flex-row gap-3">
                    <a
                      href={ctaHref}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="bmi-cta"
                      className="group flex-1 inline-flex items-center justify-center gap-3 py-3 bg-onyx text-alabaster text-xs tracking-[0.2em] uppercase font-semibold hover:bg-gold hover:text-onyx transition-colors"
                    >
                      {data.resultCtaLabel || "Quero Um Plano"}
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </a>
                    <button
                      type="button"
                      onClick={onReset}
                      data-testid="bmi-reset"
                      className="inline-flex items-center justify-center gap-2 px-4 py-3 text-xs tracking-[0.2em] uppercase font-semibold border border-onyx hover:bg-onyx hover:text-alabaster transition-colors"
                    >
                      <RotateCcw size={12} /> Refazer
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
